// Advanced accordion height management
class ProductAccordionManager {
  constructor() {
    this.accordions = document.querySelectorAll('.product__accordion details');
    this.accordionContainers = document.querySelectorAll('.product__accordion');
    this.container = document.querySelector('.product__info-container');
    this.isCalculating = false;
    this.accordionHeights = new Map();
    this.containerHeights = new Map();
    
    if (this.accordions.length && this.container) {
      this.init();
    }
  }

  init() {
    // Wait for all content to load before initializing
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAccordions());
    } else {
      this.setupAccordions();
    }
  }

  setupAccordions() {
    // Calculate heights after a brief delay to ensure all styles are applied
    setTimeout(() => {
      this.measureAccordionHeights();
      this.bindClickEvents();
      this.setInitialState();
      this.setupResizeHandler(); // Move it here, only set up once
    }, 200);
  }

  measureAccordionHeights() {
    if (this.isCalculating) return;
    this.isCalculating = true;

    // Store original states
    const originalStates = Array.from(this.accordions).map(accordion => ({
      element: accordion,
      wasOpen: accordion.hasAttribute('open')
    }));

    // Close all accordions first to measure base heights
    this.accordions.forEach(accordion => accordion.removeAttribute('open'));
    
    // Measure each accordion container's heights
    this.accordionContainers.forEach((accordionContainer, index) => {
      const accordion = accordionContainer.querySelector('details');
      const content = accordion?.querySelector('.accordion__content');
      
      if (accordion && content) {
        // Measure closed height (base height)
        accordion.removeAttribute('open');
        const closedHeight = accordionContainer.offsetHeight;
        
        // Temporarily open to measure expanded height
        const originalDisplay = content.style.display;
        const originalHeight = content.style.height;
        const originalOverflow = content.style.overflow;
        
        content.style.display = 'block';
        content.style.height = 'auto';
        content.style.overflow = 'visible';
        accordion.setAttribute('open', '');
        
        // Force reflow and measure expanded height
        const expandedHeight = accordionContainer.offsetHeight;
        
        // Store both heights
        this.containerHeights.set(accordionContainer, {
          closed: closedHeight,
          expanded: expandedHeight
        });
        
        // Reset content styles
        content.style.display = originalDisplay;
        content.style.height = originalHeight;
        content.style.overflow = originalOverflow;
        accordion.removeAttribute('open');
      }
    });

    // Set initial fixed heights on all accordion containers
    this.accordionContainers.forEach(accordionContainer => {
      const heights = this.containerHeights.get(accordionContainer);
      if (heights) {
        // Set the minimum height to the expanded height to prevent layout shifts
        accordionContainer.style.minHeight = `${heights.expanded}px`;
        accordionContainer.style.height = `${heights.closed}px`;
        accordionContainer.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        accordionContainer.style.overflow = 'hidden';
      }
    });

    // Restore original states
    originalStates.forEach(({ element, wasOpen }) => {
      if (wasOpen) {
        element.setAttribute('open', '');
        this.updateAccordionHeight(element, true);
      }
    });

    this.isCalculating = false;
  }

  updateAccordionHeight(accordion, isOpening) {
    const accordionContainer = accordion.closest('.product__accordion');
    const heights = this.containerHeights.get(accordionContainer);
    
    if (heights && accordionContainer) {
      if (isOpening) {
        // Set to expanded height
        accordionContainer.style.height = `${heights.expanded}px`;
      } else {
        // Set to closed height
        accordionContainer.style.height = `${heights.closed}px`;
      }
    }
  }

  bindClickEvents() {
    this.accordions.forEach(accordion => {
      const summary = accordion.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleClick(accordion);
        });
      }
    });
  }

  handleClick(clickedAccordion) {
    const isCurrentlyOpen = clickedAccordion.hasAttribute('open');
    const content = clickedAccordion.querySelector('.accordion__content');
    
    if (!content) return;
    
    if (isCurrentlyOpen) {
      // Closing animation
      content.style.height = content.scrollHeight + 'px';
      content.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      
      // Force reflow
      void content.offsetWidth;
      
      content.style.height = '0px';
      this.updateAccordionHeight(clickedAccordion, false);
      
      setTimeout(() => {
        clickedAccordion.removeAttribute('open');
      }, 300);
    } else {
      // Close all others first
      this.accordions.forEach(accordion => {
        if (accordion !== clickedAccordion && accordion.hasAttribute('open')) {
          const otherContent = accordion.querySelector('.accordion__content');
          if (otherContent) {
            otherContent.style.height = otherContent.scrollHeight + 'px';
            otherContent.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
            void otherContent.offsetWidth;
            otherContent.style.height = '0px';
            this.updateAccordionHeight(accordion, false);
            
            setTimeout(() => {
              accordion.removeAttribute('open');
            }, 300);
          }
        }
      });
      
      // Opening animation
      clickedAccordion.setAttribute('open', '');
      content.style.height = '0px';
      content.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      
      // Force reflow
      void content.offsetWidth;
      
      content.style.height = content.scrollHeight + 'px';
      this.updateAccordionHeight(clickedAccordion, true);
      
      setTimeout(() => {
        content.style.height = 'auto';
        content.style.transition = 'none';
      }, 300);
    }
  }

  setInitialState() {
    // Ensure only the first visible details is open by default
    let firstOpen = false;
    this.accordions.forEach((accordion) => {
      if (!firstOpen && accordion.offsetParent !== null) {
        accordion.setAttribute('open', '');
        this.updateAccordionHeight(accordion, true);
        firstOpen = true;
      } else {
        accordion.removeAttribute('open');
        this.updateAccordionHeight(accordion, false);
      }
    });
  }

  setupResizeHandler() {
    let resizeTimeout;
    
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const currentWidth = this.container.offsetWidth;
        const widthDifference = Math.abs(currentWidth - (this.lastWidth || 0));
        
        // Debug: uncomment to see resize detection
        // console.log('Resize detected:', { currentWidth, lastWidth: this.lastWidth, difference: widthDifference });
        
        // Recalculate if width changed significantly or if it's the first time
        if (!this.lastWidth || widthDifference > 50) {
          this.lastWidth = currentWidth;
          
          // Store which accordion is currently open
          const openAccordion = Array.from(this.accordions).find(acc => acc.hasAttribute('open'));
          
          // Debug: uncomment to see recalculation
          // console.log('Recalculating heights for width:', currentWidth, 'Open accordion:', openAccordion);
          
          // Temporarily disable transitions to prevent flashing
          this.accordionContainers.forEach(container => {
            container.style.transition = 'none';
          });
          
          // Recalculate all heights
          this.measureAccordionHeights();
          
          // Re-enable transitions after a brief moment
          setTimeout(() => {
            this.accordionContainers.forEach(container => {
              container.style.transition = 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
            });
          }, 50);
          
          // Restore the open state and update heights immediately
          if (openAccordion) {
            setTimeout(() => {
              openAccordion.setAttribute('open', '');
              this.updateAccordionHeight(openAccordion, true);
              
              // Update content height for responsive content
              const content = openAccordion.querySelector('.accordion__content');
              if (content) {
                content.style.height = 'auto';
                content.style.transition = 'none';
              }
            }, 100);
          }
        }
      }, 300);
    };
    
    // Store initial width
    this.lastWidth = this.container.offsetWidth;
    
    // Listen for both resize and orientation change events
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      // Orientation change needs a longer delay to ensure layout is complete
      setTimeout(handleResize, 500);
    });
  }
} 

// Initialize the accordion manager
new ProductAccordionManager();
